import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import classNames from 'classnames/bind';
import { addDays, format, startOfDay, startOfToday } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { createPaymentRedirect } from '../../../../helper/createPaymentRedirect';
import { handlePaymentRedirect } from '../../../../helper/handlePaymentRedirect';
import { isPrescriptionWindowOpen } from '../../../../helper/isPrescriptionWindowOpen';
import { setPendingPayment } from '../../../../helper/paymentPendingStorage';
import UserService from '../../../../services/User.service';
import { useAppDispatch } from '../../../../store/hooks';
import { loadUserInformation } from '../../../../store/reducers/userReducers';
import type { UserInformation } from '../../../../types/user.types';
import { Button } from '../../../atoms';
import style from './StageElements.module.scss';

type FollowUpPrescriptionProps = {
  userInformation: UserInformation;
};

const cx = classNames.bind(style);

const dividerClasses = cx({ Divider: true });
const descriptionSectionClasses = cx({ DescriptionSection: true });
const descriptionTitleClasses = cx({ DescriptionTitle: true });
const descriptionTextClasses = cx({ DescriptionText: true });
const actionButtonsClasses = cx({ ActionButtons: true });

export const FollowUpPrescription = (props: FollowUpPrescriptionProps) => {
  const { userInformation } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const lastRequest =
    userInformation?.followUpPrescriptionRequest &&
    userInformation?.followUpPrescriptionRequest[userInformation.followUpPrescriptionRequest.length - 1];

  const lastRequestDate =
    lastRequest && lastRequest.updateDate !== undefined ? new Date(lastRequest.updateDate) : undefined;

  const allAppointments = userInformation?.appointment || [];

  const carriedOutAppointments = allAppointments.filter((appointment) => appointment && appointment.carriedOut === 1);

  const lastAppointment =
    carriedOutAppointments.length > 0 ? carriedOutAppointments[carriedOutAppointments.length - 1] : undefined;

  const lastAppointmentStartValue = lastAppointment?.startDate;
  const lastAppointmentDate =
    lastAppointment && lastAppointmentStartValue !== undefined ? new Date(lastAppointmentStartValue) : undefined;

  const mostRecentEventDate: Date | undefined = (() => {
    if (lastRequestDate && lastAppointmentDate) {
      return lastRequestDate.getTime() >= lastAppointmentDate.getTime() ? lastRequestDate : lastAppointmentDate;
    }

    return lastRequestDate || lastAppointmentDate;
  })();

  const prescriptionAvailableFromDate = mostRecentEventDate ? startOfDay(addDays(mostRecentEventDate, 3)) : undefined;

  const today = startOfToday();
  const dateAcceptedByDoctor = lastRequest?.updateDate ? new Date(lastRequest.updateDate) : undefined;
  const hours48 = dateAcceptedByDoctor && startOfDay(addDays(dateAcceptedByDoctor, 2));

  const isLastRequestAcceptedAndWithin48Hours =
    (lastRequest?.status === 'accepted' && hours48 && today <= hours48) || false;

  const isLastRequestStatusUndefined = lastRequest && lastRequest.status === undefined;
  const isLastRequestPending = lastRequest?.status === 'paymentPending';

  const canRequestPrescription = isPrescriptionWindowOpen(userInformation);

  const PrescriptionIsSend = () => (
    <>
      <div className={descriptionSectionClasses}>
        <div className={dividerClasses} />
        <div className={descriptionTitleClasses}>Ihr Folgerezept ist unterwegs</div>
        <div className={descriptionTextClasses}>
          Ihr Folgerezept wurde ausgestellt und wird an Ihre Apotheke übermittelt.
        </div>
      </div>
    </>
  );

  const [isPaying, setPaying] = useState(false);
  const [isStartingNew, setStartingNew] = useState(false);

  const RequestPending = () => {
    const payPrescription = async () => {
      setPaying(true);
      try {
        const redirect = createPaymentRedirect();
        const response = await UserService.payPrescription(lastRequest.id, redirect);
        if (response.error || !response.data?.redirectUrl) {
          console.error('API Error:', response);
          toast.error('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
        } else {
          setPendingPayment({
            startedAt: Date.now(),
            sourcePath: `${window.location.pathname}${window.location.search}`,
            novalnetUrl: response.data.redirectUrl,
            contextType: 'prescription',
            contextId: lastRequest.id,
          });
          handlePaymentRedirect(response.data.redirectUrl);

          return;
        }
      } catch (error) {
        console.error('API Error:', error);
        toast.error('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      } finally {
        setPaying(false);
      }
    };

    const startNewRequest = async () => {
      setStartingNew(true);
      try {
        await UserService.expirePendingPrescriptionRequests();
        await loadUserInformation(dispatch);
        navigate('/follow-up-prescription');
      } catch (error) {
        console.error('Failed to start new request:', error);
        toast.error('Ein neuer Antrag konnte nicht gestartet werden. Bitte versuchen Sie es erneut.');
      } finally {
        setStartingNew(false);
      }
    };

    return (
      <div className={descriptionSectionClasses}>
        <div className={dividerClasses} />
        <div className={descriptionTitleClasses}>Ihr Folgerezept</div>
        <div className={descriptionTextClasses}>
          Bitte schließen Sie die Zahlung für Ihr Folgerezept ab oder beantragen Sie ein neues Rezept.
        </div>
        <div className={actionButtonsClasses}>
          <Button disabled={isPaying || isStartingNew} onClick={payPrescription}>
            {isPaying ? <Loader2 className='animate-spin' /> : 'Zur Zahlung'}
          </Button>
          <Button secondary disabled={isPaying || isStartingNew} onClick={startNewRequest}>
            {isStartingNew ? <Loader2 className='animate-spin' /> : 'Neues Folgerezept beantragen'}
          </Button>
        </div>
      </div>
    );
  };

  const RequestMade = () => (
    <div className={descriptionSectionClasses}>
      <div className={dividerClasses} />
      <div className={descriptionTitleClasses}>Ihr Folgerezept</div>
      <div className={descriptionTextClasses}>
        Ihr Folgerezept ist angefordert und wird von Ihrem Arzt / Ihrer Ärztin geprüft.
      </div>
    </div>
  );

  const RequestAccepted = () => (
    <div className={descriptionSectionClasses}>
      <div className={dividerClasses} />
      <div className={descriptionTitleClasses}>Ihr Folgerezept</div>
      <div className={descriptionTextClasses}>
        Ihr Folgerezept ist in Bearbeitung. Nach der Ausstellung durch Ihren Arzt / Ihrer Ärztin werden Sie informiert.
      </div>
    </div>
  );

  const MakeRequest = () => {
    if (canRequestPrescription) {
      return (
        <div className={descriptionSectionClasses}>
          <div className={dividerClasses} />
          <div className={descriptionTitleClasses}>Folgerezept anfordern</div>
          <div className={descriptionTextClasses}>Fordern Sie ein Folgerezept für Ihre aktuelle Medikation an.</div>
          <div className={actionButtonsClasses}>
            <Button secondary={true} onClick={() => navigate('/follow-up-prescription')}>
              Rezept anfordern
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={descriptionSectionClasses}>
        <div className={dividerClasses} />
        <div className={descriptionTitleClasses}>Folgerezept nicht verfügbar</div>
        <div className={descriptionTextClasses}>
          Um Ihre Therapie bestmöglich auf Sie abzustimmen und den Behandlungserfolg zu überprüfen, möchte Ihr Arzt ein
          Video-Gespräch mit Ihnen führen. Bitte buchen Sie hierfür eine Videokonsultation.
        </div>
        <div className={descriptionTextClasses}>
          Ohne ein vorheriges Video-Gespräch, ist die Ausstellung eines Folgerezepts leider nicht möglich.
        </div>
      </div>
    );
  };

  const AfterThisDateYouCanMakeTheRequest = () => (
    <div className={descriptionSectionClasses}>
      <div className={dividerClasses} />
      <div className={descriptionTitleClasses}>Folgerezept</div>
      <div className={descriptionTextClasses}>
        Ab dem {''}
        {prescriptionAvailableFromDate && format(new Date(prescriptionAvailableFromDate), 'dd.MM.yyyy')} {''}
        können Sie ein Folgerezept anfordern.
      </div>
    </div>
  );

  const isPrescriptionAvailable = prescriptionAvailableFromDate && prescriptionAvailableFromDate <= today;

  if (isLastRequestPending) {
    return <RequestPending />;
  }

  if (isLastRequestStatusUndefined) {
    return <RequestMade />;
  }

  if (isLastRequestAcceptedAndWithin48Hours) {
    if (!lastRequest?.prescriptionDone) {
      return <RequestAccepted />;
    }

    return <PrescriptionIsSend />;
  }

  if (!isPrescriptionAvailable) {
    return <AfterThisDateYouCanMakeTheRequest />;
  }

  return <MakeRequest />;
};
